// packages/web/src/components/ui/PageHeader.tsx — NUEVO. Encabezado común de las vistas docentes.
import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';

interface Props {
  title: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: ReactNode;
  back?: { label: string; onClick: () => void };
  actions?: ReactNode;
}

export function PageHeader({ title, subtitle, eyebrow, back, actions }: Props) {
  return (
    <div className="flex flex-col gap-3">
      {back && (
        <button type="button" onClick={back.onClick}
          className="inline-flex items-center gap-1.5 self-start rounded-md text-sm text-slate-600 hover:text-marzano-600 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500">
          <ArrowLeft aria-hidden className="w-4 h-4" />{back.label}
        </button>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <div className="text-[13px] text-slate-600 mb-0.5">{eyebrow}</div>}
          <h1 className="font-serif text-[32px] leading-tight text-marzano-600">{title}</h1>
          {subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

/** Badge de estado: siempre icono/punto + texto (nunca solo color). */
export function StatusBadge({ tone, children }: { tone: 'ok' | 'info' | 'warn' | 'danger' | 'muted'; children: ReactNode }) {
  const cls = {
    ok: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    info: 'bg-marzano-50 text-marzano-700 border-marzano-100',
    warn: 'bg-amber-50 text-amber-900 border-amber-200',
    danger: 'bg-red-50 text-red-800 border-red-200',
    muted: 'bg-slate-100 text-slate-700 border-slate-200',
  }[tone];
  const dot = { ok: 'bg-emerald-600', info: 'bg-marzano-500', warn: 'bg-amber-500', danger: 'bg-red-600', muted: 'bg-slate-400' }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-semibold ${cls}`}>
      <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${dot}`} />{children}
    </span>
  );
}

/** Cifra resumen (KPI). */
export function Stat({ label, value, hint, tone = 'default' }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: 'default' | 'warn' }) {
  return (
    <div className={`rounded-xl border px-4 py-3.5 ${tone === 'warn' ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'}`}>
      <div className={`text-[13px] ${tone === 'warn' ? 'font-semibold text-amber-900' : 'text-slate-600'}`}>{label}</div>
      <div className={`text-[26px] font-bold tabular-nums leading-tight ${tone === 'warn' ? 'text-amber-900' : 'text-slate-900'}`}>{value}</div>
      {hint && <div className="text-xs text-slate-600 mt-0.5">{hint}</div>}
    </div>
  );
}

/** Tabla base: cabecera sobria, filas con borde y hover. */
export const TABLE = {
  wrap: 'bg-white rounded-xl border border-slate-200 overflow-x-auto',
  table: 'w-full text-sm',
  thead: 'bg-slate-50 text-[12px] uppercase tracking-wider text-slate-600',
  th: 'text-left px-4 py-2.5 font-semibold whitespace-nowrap',
  tr: 'border-t border-slate-100 hover:bg-slate-50/70',
  td: 'px-4 py-3 align-middle',
};

export function shortDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  const time = d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `Hoy, ${time}`;
  if (d.toDateString() === yest.toDateString()) return `Ayer, ${time}`;
  return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', ...(d.getFullYear() !== now.getFullYear() && { year: 'numeric' }) });
}
